import React from 'react';
import { Alert, Box, Typography } from '@mui/material';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const STATUS_META = [
  { key: 'PENDING', label: 'Pending', color: '#64748b' },
  { key: 'RUNNING', label: 'Running', color: '#38bdf8' },
  { key: 'AWAITING_REVIEW', label: 'Awaiting review', color: '#a78bfa' },
  { key: 'COMPLETED', label: 'Completed', color: '#10b981' },
  { key: 'REJECTED', label: 'Rejected', color: '#ef4444' },
  { key: 'FAILED', label: 'Failed', color: '#f59e0b' },
];

function Panel({ title, hint, children, sx }) {
  return (
    <Box
      sx={{
        background: '#1e293b',
        border: '1px solid #243044',
        borderRadius: '8px',
        px: 2.5,
        py: 2.25,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#e2e8f0' }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: '0.6875rem', color: '#64748b' }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function KpiCard({ label, value, hint, highlight, warning }) {
  return (
    <Box
      sx={{
        background: highlight ? '#172554' : '#1e293b',
        boxShadow: highlight ? 'inset 3px 0 0 #38bdf8' : 'none',
        px: 2.5,
        py: 2.25,
        minHeight: 108,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#64748b',
          mb: 1.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: '1.75rem',
          fontWeight: 500,
          letterSpacing: '-0.02em',
          lineHeight: 1,
          color: highlight ? '#6ee7b7' : '#e2e8f0',
        }}
      >
        {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
      </Typography>
      {hint && (
        <Typography sx={{ mt: 1.25, fontSize: '0.75rem', color: '#64748b' }}>{hint}</Typography>
      )}
      {warning && (
        <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: '#fbbf24' }}>{warning}</Typography>
      )}
    </Box>
  );
}

function StatusRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr 52px',
        alignItems: 'center',
        gap: 1.5,
        py: 1.1,
      }}
    >
      <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{label}</Typography>
      <Box sx={{ height: 8, background: '#0f172a', borderRadius: 999, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999 }} />
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right', color: '#e2e8f0' }}>
        {count.toLocaleString()}
      </Typography>
    </Box>
  );
}

function FunnelStep({ num, title, body, value, sub, hero }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        gap: 1.5,
        alignItems: 'start',
        py: 1.5,
        borderBottom: '1px solid rgba(148,163,184,0.08)',
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: '#64748b', pt: 0.35 }}>{num}</Typography>
      <Box>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: hero ? '#10b981' : '#e2e8f0', mb: 0.4 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.45 }}>{body}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', fontFamily: MONO }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color: hero ? '#10b981' : '#e2e8f0', lineHeight: 1.1 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {sub != null && (
          <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{sub}</Typography>
        )}
      </Box>
    </Box>
  );
}

// Reviewer edit kinds from heal_jobs.review_context.reviewer_edit (DPS-4702).
const EDIT_KIND_LABELS = {
  entry_added: 'Added an entry',
  entry_removed: 'Removed an entry',
  entries_reordered: 'Reordered entries',
  selector_changed: 'Changed the selector',
  select_mode_changed: 'Changed select mode',
  method_changed: 'Changed the method',
  parsers_changed: 'Changed parsers',
  other_entry_change: 'Other entry change',
  parsed_config_changed: 'Changed parsed config',
};

// First day reviewer_edit is recorded; older approvals carry no kinds.
export const EDIT_KINDS_RECORDED_FROM = '3 Nov 2026';

export function editKindLabel(kind) {
  return EDIT_KIND_LABELS[kind] || kind;
}

function formatEditKinds(kinds) {
  if (!Array.isArray(kinds) || kinds.length === 0) return '—';
  return kinds.map((k) => `${editKindLabel(k.kind)} (${k.count})`).join(', ');
}

function EditKindsList({ kinds }) {
  if (!kinds.length) {
    return (
      <Typography sx={{ fontSize: '0.78rem', color: '#64748b', py: 1 }}>
        {`No classified reviewer edits in this range yet. Edits are recorded from ${EDIT_KINDS_RECORDED_FROM}.`}
      </Typography>
    );
  }
  return (
    <Box>
      {kinds.map((k) => (
        <Box
          key={k.kind}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            py: 1,
            borderBottom: '1px solid rgba(148,163,184,0.08)',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{editKindLabel(k.kind)}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 500, color: '#e2e8f0' }}>
            {Number(k.count ?? 0).toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function formatPct(value) {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
}

const DEFAULT_SERIES = [
  { key: 'triggered', label: 'Triggered', color: 'rgba(56,189,248,0.55)' },
  { key: 'approved', label: 'Approved', color: 'rgba(16,185,129,0.75)' },
];

const PR_SERIES = [
  { key: 'raised', label: 'Raised', color: 'rgba(56,189,248,0.55)' },
  { key: 'merged', label: 'Merged', color: 'rgba(16,185,129,0.75)' },
  { key: 'auto_merged', label: 'Auto-merged', color: 'rgba(148,163,184,0.7)' },
];

function DailyBars({ points, series = DEFAULT_SERIES }) {
  const maxVal = Math.max(1, ...points.flatMap((p) => series.map((s) => p[s.key] ?? 0)));
  const sample = points.length <= 10
    ? points
    : points.filter((_, i) => i % Math.ceil(points.length / 8) === 0 || i === points.length - 1);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 120 }}>
        {sample.map((p) => (
          <Box
            key={p.date}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', width: '100%', height: '100%' }}>
              {series.map((s) => (
                <Box
                  key={s.key}
                  sx={{
                    flex: 1,
                    height: `${Math.max(2, ((p[s.key] ?? 0) / maxVal) * 100)}%`,
                    background: s.color,
                    borderRadius: '2px 2px 0 0',
                  }}
                />
              ))}
            </Box>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.58rem', color: '#64748b' }}>
              {p.date.slice(8)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, fontSize: '0.7rem', color: '#64748b' }}>
        {series.map((s) => (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color }} />
            {s.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SimpleTable({ columns, rows }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
      <Box component="thead">
        <Box component="tr">
          {columns.map((c) => (
            <Box
              component="th"
              key={c.key}
              sx={{
                textAlign: c.align || 'left',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#64748b',
                fontWeight: 500,
                pb: 1.25,
                borderBottom: '1px solid #243044',
                pr: 1,
              }}
            >
              {c.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.length === 0 ? (
          <Box component="tr">
            <Box component="td" colSpan={columns.length} sx={{ py: 2, color: '#64748b' }}>
              No data in range
            </Box>
          </Box>
        ) : (
          rows.map((row) => (
            <Box component="tr" key={row._key}>
              {columns.map((c) => (
                <Box
                  component="td"
                  key={c.key}
                  sx={{
                    py: 1.25,
                    pr: 1,
                    borderBottom: '1px solid rgba(148,163,184,0.08)',
                    textAlign: c.align || 'left',
                    fontFamily: c.mono ? MONO : 'inherit',
                    color: c.key === columns[0].key ? '#e2e8f0' : '#94a3b8',
                    fontWeight: c.key === columns[0].key ? 500 : 400,
                  }}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export default function HealingMetricsPanel({ metrics }) {
  if (!metrics) return null;

  const triggered = metrics.jobs_triggered ?? 0;
  // DPS-4717: jobs_triggered leaves out out-of-scope and false-positive jobs (not
  // real heal attempts); jobs_total counts every job. Older backends send no
  // jobs_total, and their jobs_triggered counts every job.
  const jobsTotal = metrics.jobs_total ?? triggered;
  const notRealJobs = (metrics.out_of_scope ?? 0) + (metrics.false_positive ?? 0);
  const byStatus = metrics.by_status || {};
  const approved = metrics.approved ?? 0;
  const withEdits = metrics.approved_with_edits ?? 0;
  const withoutModal = metrics.approved_without_modal_edits ?? 0;
  const pathB = metrics.path_b_config_commits ?? 0;
  const untouched = metrics.pr_merged_untouched ?? 0;
  const acceptance = metrics.agent_acceptance_pct;
  const prsRaised = metrics.prs_raised ?? 0;
  const reverted = metrics.auto_merge_reverted ?? 0;
  const regressions = metrics.post_merge_regression ?? 0;
  // Older backends send no edit_kinds: hide the edit UI rather than show zeros.
  const hasEditKinds = Array.isArray(metrics.edit_kinds);
  const autoMergeWarning = [
    reverted > 0 ? `${reverted} reverted` : null,
    regressions > 0 ? `${regressions} ${regressions === 1 ? 'regression' : 'regressions'}` : null,
  ].filter(Boolean).join(' · ') || null;

  return (
    <Box>
      {!metrics.table_ready && (
        <Alert severity="info" sx={{ mb: 2 }}>
          heal_jobs table not found yet — metrics will populate after the healing agent migration.
        </Alert>
      )}
      {!metrics.webhook_fields_available && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, fontSize: '0.8rem' }}>
          GitHub webhook fields not seen yet. Path B (config commits on PR) and merged-untouched
          counts stay at 0 until PR status/commit webhooks are ingested. Path A (Edit Config modal)
          is tracked from approvals now.
        </Alert>
      )}

      {metrics.prs_raised != null && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: '1px',
            background: '#243044',
            border: '1px solid #243044',
            borderRadius: '8px',
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <KpiCard label="PRs raised" value={prsRaised} hint="Heal PRs created in range" />
          <KpiCard label="Merged (auto + human)" value={metrics.prs_merged ?? 0} hint="Merged PRs from those jobs" />
          <KpiCard
            label="Auto-merged"
            value={metrics.prs_auto_merged ?? 0}
            hint={`Auto-merged ÷ raised: ${formatPct(metrics.auto_merged_over_raised_pct)}`}
            warning={autoMergeWarning}
          />
          <KpiCard label="Human-merged" value={metrics.prs_human_merged ?? 0} hint="Merged after a reviewer approved" />
          <KpiCard
            label="Merged / raised"
            value={formatPct(metrics.merged_over_raised_pct)}
            hint="Goal: 90%"
            highlight
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: '1px',
          background: '#243044',
          border: '1px solid #243044',
          borderRadius: '8px',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <KpiCard
          label="Heal jobs triggered"
          value={triggered}
          hint={
            metrics.jobs_total != null && notRealJobs > 0
              ? `Excludes ${notRealJobs.toLocaleString()} out of scope / false positive`
              : 'Unique job starts in range'
          }
        />
        <KpiCard
          label="Approved"
          value={approved}
          hint={triggered > 0 ? `${Math.round((approved / triggered) * 1000) / 10}% of triggered` : null}
        />
        <KpiCard
          label="PRs merged · agent config untouched"
          value={untouched}
          hint={
            metrics.webhook_fields_available
              ? `${formatPct(acceptance)} of merged PRs · no modal edit + no config commit`
              : 'Requires webhook merge signal'
          }
          highlight
        />
        <KpiCard
          label="Agent acceptance rate"
          value={acceptance == null ? '—' : `${acceptance}%`}
          hint={
            metrics.webhook_fields_available
              ? 'Merged untouched ÷ PRs merged'
              : 'Fallback: approved without modal edits ÷ approved'
          }
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Panel title="Jobs by status" hint={`${jobsTotal.toLocaleString()} total in range`}>
          {STATUS_META.map((s) => (
            <StatusRow
              key={s.key}
              label={s.label}
              count={byStatus[s.key] || 0}
              total={jobsTotal}
              color={s.color}
            />
          ))}
          {metrics.out_of_scope != null && (
            // DPS-4717: COMPLETED upstream with no repair attempted; not in Completed above.
            <StatusRow label="Out of scope" count={metrics.out_of_scope} total={jobsTotal} color="#94a3b8" />
          )}
          {metrics.false_positive != null && (
            // DPS-4717: COMPLETED upstream, no PR: the anomaly was not real.
            <StatusRow label="False positive" count={metrics.false_positive} total={jobsTotal} color="#a5b4fc" />
          )}
          {metrics.skipped_by_gate != null && (
            // REJECTED upstream by a relevance gate, not by a reviewer; not in Rejected above.
            <StatusRow label="Skipped by gate" count={metrics.skipped_by_gate} total={jobsTotal} color="#64748b" />
          )}
        </Panel>

        <Panel title="Agent config purity funnel" hint="Where humans touch the agent output">
          <FunnelStep
            num="01"
            title="Approved jobs"
            body="Human clicked Approve / Approve with Edits and a PR was created."
            value={approved}
            sub="100%"
          />
          <FunnelStep
            num="02"
            title="Approved without Edit Config modal"
            body="No edited_config on approve — agent config left unchanged in UI."
            value={withoutModal}
            sub={approved > 0 ? `${Math.round((withoutModal / approved) * 1000) / 10}%` : '—'}
          />
          <FunnelStep
            num="03"
            title="PR merged · agent config untouched"
            body="Modal untouched and no webhook-recorded config commits (ignores update-branch)."
            value={untouched}
            sub={approved > 0 ? `${Math.round((untouched / approved) * 1000) / 10}%` : '—'}
            hero
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.75 }}>
            <Box sx={{ background: '#0f172a', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', px: 1.75, py: 1.5 }}>
              <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', mb: 0.75 }}>
                Path A — Edit Config modal
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '1.25rem', fontWeight: 600, color: '#f59e0b' }}>
                {withEdits.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 0.5 }}>
                Approved with edits in UI before PR
              </Typography>
            </Box>
            <Box sx={{ background: '#0f172a', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', px: 1.75, py: 1.5 }}>
              <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', mb: 0.75 }}>
                Path B — Config commits on PR
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '1.25rem', fontWeight: 600, color: '#f59e0b' }}>
                {pathB.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 0.5 }}>
                Via GitHub webhook (excl. update-branch)
              </Typography>
            </Box>
          </Box>
        </Panel>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: '1px',
          background: '#243044',
          border: '1px solid #243044',
          borderRadius: '8px',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <KpiCard
          label="Median time to review"
          value={
            metrics.median_time_to_review_hours == null
              ? '—'
              : `${metrics.median_time_to_review_hours}h`
          }
          hint="Awaiting review → approve / reject / revise"
        />
        <KpiCard label="PRs still open" value={metrics.prs_open ?? 0} hint="From webhook · opened, not merged/closed" />
        <KpiCard label="PRs merged (webhook)" value={metrics.prs_merged ?? 0} hint="pull_request.closed + merged=true" />
        <KpiCard
          label="Jobs with ≥1 revise"
          value={metrics.jobs_with_revise ?? 0}
          hint={triggered > 0 ? `${Math.round(((metrics.jobs_with_revise ?? 0) / triggered) * 1000) / 10}% of triggered` : null}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <Panel title="Daily volume" hint="Triggered vs approved">
          <DailyBars points={metrics.daily_volume || []} />
        </Panel>

        {metrics.prs_raised != null && (
          <Panel title="Daily PRs" hint="Raised vs merged, by job start day">
            <DailyBars points={metrics.daily_volume || []} series={PR_SERIES} />
          </Panel>
        )}

        <Panel title="Top retailers" hint="By jobs + untouched merge %">
          <SimpleTable
            columns={[
              { key: 'retailer', label: 'Retailer' },
              { key: 'jobs', label: 'Jobs', align: 'right', mono: true },
              { key: 'approved', label: 'Approved', align: 'right', mono: true },
              {
                key: 'untouched_merged_pct',
                label: 'Untouched',
                align: 'right',
                mono: true,
                render: (r) => formatPct(r.untouched_merged_pct),
              },
            ]}
            rows={(metrics.by_retailer || []).map((r) => ({ ...r, _key: r.retailer }))}
          />
        </Panel>

        <Panel title="Top attributes" hint="Where agent needs the most help">
          <SimpleTable
            columns={[
              { key: 'attribute', label: 'Attribute' },
              { key: 'jobs', label: 'Jobs', align: 'right', mono: true },
              { key: 'modal_edits', label: 'Modal edits', align: 'right', mono: true },
              ...(hasEditKinds
                ? [
                  {
                    key: 'edit_rate_pct',
                    label: 'Edit rate',
                    align: 'right',
                    mono: true,
                    render: (r) => formatPct(r.edit_rate_pct),
                  },
                  {
                    key: 'top_edit_kinds',
                    label: 'Top edit kinds',
                    render: (r) => formatEditKinds(r.top_edit_kinds),
                  },
                ]
                : []),
              {
                key: 'untouched_merged_pct',
                label: 'Untouched',
                align: 'right',
                mono: true,
                render: (r) => formatPct(r.untouched_merged_pct),
              },
            ]}
            rows={(metrics.by_attribute || []).map((r) => ({ ...r, _key: r.attribute }))}
          />
        </Panel>

        {hasEditKinds && (
          <Panel title="What reviewers change" hint="Counts are per kind; one approval can have several kinds.">
            <EditKindsList kinds={metrics.edit_kinds} />
          </Panel>
        )}
      </Box>
    </Box>
  );
}
